import React, { useState, useEffect } from 'react';
import {
  getOperationApp,
  getOperationWeb,
} from '../../../service/dashboard/Dashboard';
import { TopUpBalanceModal } from '../../../../_mexablock/partials/modals/balance/TopUpBalanceModal';
import { useSelectOffers } from '../../operacion/activacion/hooks/ActivacionHook';
import { MovInventory } from './MovInventory';
import Indicators from './Indicators';
import { OfferDashboard } from './OfferDashboard';
import { GraficaBalance } from './GraficaBalance';
import MisPlanes from './MisPlanes';
import { UserModel } from '../../../modules/auth/models/UserModel';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { SalesByChannelCard } from './MetricasAppWeb';
import { ProvisionBalanceCard } from './ProvisionBalanceCard';
import { Filter } from './FilterDashboard';

const DashboardPage: React.FC = () => {
  const user: UserModel = useSelector<RootState>(
    ({ auth }) => auth.user,
    shallowEqual,
  ) as UserModel;

  const userRoles =
    (user?.roles as Array<{
      id?: number | null;
      name?: string | null;
      code?: string | null;
    }>) || [];
  let fechaActual = new Date();
  let fecha_inicio_init = new Date(
    fechaActual.getFullYear(),
    fechaActual.getMonth(),
    1,
  );
  let fecha_fin_init = new Date();
  const [dateState, setDateState] = useState<string[]>([]);
  const [initFilterValues, setInitFilterValues] = useState({
    offer_id: '',
    user_id: '',
    init_date: new Date(fecha_inicio_init).toISOString().split('T')[0],
    end_date: new Date(fecha_fin_init).toISOString().split('T')[0],
    filter: 0,
  });
  const [visivilityModal, setVisivilityModal] = useState(false);
  const [offersCache, setOffersCache] = useState<any[]>([]);
  const [appData, setAppData] = useState({
    recharges_amount: 0,
    activations_amount: 0,
    portabilities_amount: 0,
  });
  const [webData, setWebData] = useState({
    recharges_amount: 0,
    activations_amount: 0,
    portabilities_amount: 0,
  });
  const { data: offers, loading } = useSelectOffers(
    offersCache.length === 0,
    'recharge',
  );

  useEffect(() => {
    if (offers.length > 0 && offersCache.length === 0) {
      setOffersCache(offers);
    }
  }, [offers]);

  // Cargar datos de operaciones App y Web
  useEffect(() => {
    const fetchOperations = async () => {
      try {
        const filters = {
          init_date: initFilterValues.init_date,
          end_date: initFilterValues.end_date,
        };
        const appResponse = await getOperationApp(filters);
        if (appResponse) {
          setAppData(appResponse);
        }

        const webResponse = await getOperationWeb(filters);
        if (webResponse) {
          setWebData(webResponse);
        }
      } catch (error) {
        console.error('Error fetching operations:', error);
      }
    };
    fetchOperations();
  }, [initFilterValues.init_date, initFilterValues.end_date]);

  const userHasRole = (rolesToCheck: string[]): boolean => {
    if (!userRoles || userRoles.length === 0) return false;

    const userRoleCodes = userRoles.map(
      (r: any) => r.code?.toUpperCase() || '',
    );

    return rolesToCheck.some((role) =>
      userRoleCodes.includes(role.toUpperCase()),
    );
  };

  const isAdminMexablock = userHasRole(['ADMIN_MEXABLOCK']);
  const isCompanyOrPartnerAdmin = userHasRole([
    'ADMIN_COMPANY',
    'ADMIN_PARTNER',
  ]);
  const canSeeMainDashboardSections = isAdminMexablock || isCompanyOrPartnerAdmin;

  const handleFilter = (values: any) => {
    setInitFilterValues(values);
    if (values) {
      setDateState([values.init_date, values.end_date]);
    }
  };

  const handleFilterClean = () => {
    setInitFilterValues((prev) => ({
      ...prev,
      init_date: new Date(fecha_inicio_init).toISOString().split('T')[0],
      end_date: new Date(fecha_fin_init).toISOString().split('T')[0],
    }));
    setDateState([]);
  };

  type MetricItem = {
    key: string;
    label: string;
    value: number;
    color?: string; // texto
    iconBg?: string; // fondo del icono
    icon?: React.ReactNode; // svg o <i/>
  };

  const BoltIcon = ({ color = '#0D6EFD' }: { color?: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M13 2L3 14h7l-1 8 12-14h-8l0-6z" fill={color} />
    </svg>
  );

  const UserPlusIcon = ({ color = '#7B3FF2' }: { color?: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M15 7a4 4 0 11-8 0 4 4 0 018 0z" fill={color} opacity="0.9" />
      <path d="M4 20a7 7 0 0114 0v1H4v-1z" fill={color} opacity="0.35" />
      <path d="M19 8v2h-2v2h2v2h2v-2h2v-2h-2V8h-2z" fill={color} />
    </svg>
  );

  const SwapIcon = ({ color = '#F36B2C' }: { color?: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M7 7h14v2H7l3-3-1.4-1.4L3.2 10l5.4 5.4L10 14l-3-3z"
        fill={color}
        opacity="0.9"
      />
      <path
        d="M17 17H3v-2h14l-3 3 1.4 1.4 5.4-5.4-5.4-5.4L14 10l3 3z"
        fill={color}
        opacity="0.45"
      />
    </svg>
  );

  // Métricas para App
  const appTop: MetricItem = {
    key: 'recharges',
    label: 'Recargas',
    value: appData.recharges_amount,
    color: '#0D6EFD',
    iconBg: '#E8F4FF',
    icon: <BoltIcon />,
  };

  const appLeft: MetricItem = {
    key: 'activations',
    label: 'Activaciones',
    value: appData.activations_amount,
    color: '#7B3FF2',
    iconBg: '#EFE7FF',
    icon: <UserPlusIcon />,
  };

  const appRight: MetricItem = {
    key: 'port',
    label: 'Portabilidades',
    value: appData.portabilities_amount,
    color: '#FFBB00',
    iconBg: '#FFF4CC',
    icon: <SwapIcon />,
  };

  // Métricas para Web
  const webTop: MetricItem = {
    key: 'recharges',
    label: 'Recargas',
    value: webData.recharges_amount,
    color: '#0D6EFD',
    iconBg: '#E8F4FF',
    icon: <BoltIcon />,
  };

  const webLeft: MetricItem = {
    key: 'activations',
    label: 'Activaciones',
    value: webData.activations_amount,
    color: '#7B3FF2',
    iconBg: '#EFE7FF',
    icon: <UserPlusIcon />,
  };

  const webRight: MetricItem = {
    key: 'port',
    label: 'Portabilidades',
    value: webData.portabilities_amount,
    color: '#FFBB00',
    iconBg: '#FFF4CC',
    icon: <SwapIcon />,
  };

  return (
    <>
      <div className="row w-100 gx-2 justify-content-end me-3">
        <div className="col-md-2 text-end w-300px mb-5">
          <Filter
            initFilterValues={initFilterValues}
            onCleanFilter={handleFilterClean}
            onSearchFilter={handleFilter}
          />
        </div>
      </div>
      <Indicators
        init_date={initFilterValues.init_date}
        end_date={initFilterValues.end_date}
      />

      {canSeeMainDashboardSections && (
        <div className="row g-4 mt-5 mb-5">
          <div className="col-12 col-xl-4">
            <OfferDashboard
              init_date={initFilterValues.init_date}
              end_date={initFilterValues.end_date}
            />
          </div>

          {isAdminMexablock && (
            <>
            <div className="col-12 col-xl-4">
              <MovInventory
                init_date={initFilterValues.init_date}
                end_date={initFilterValues.end_date}
              />
            </div>
          <div className="col-12 col-xl-4 d-flex flex-column gap-4">
            <SalesByChannelCard
              title="Ventas por App"
              top={appTop}
              bottomLeft={appLeft}
              bottomRight={appRight}
            />
            <SalesByChannelCard
              title="Ventas por Web"
              top={webTop}
              bottomLeft={webLeft}
              bottomRight={webRight}
            />
          </div>
            
            </>
          )}

        </div>
      )}

      {isAdminMexablock && (
        <div className="row mb-9 mt-5">
          <div className="col-8">
            <GraficaBalance />
          </div>
          <div className="col-4">
            <ProvisionBalanceCard />
          </div>
        </div>
      )}
      <div className="mt-5 mb-5">
        <MisPlanes offers={offersCache} loading={loading} />
      </div>
      <TopUpBalanceModal
        show={visivilityModal}
        handleClose={(updateData: boolean) => {
          setVisivilityModal(!visivilityModal);
        }}
      />
    </>
  );
};

export { DashboardPage };
